<script lang="ts" setup>
/**
 * @name Dashboard.vue
 * @version 1.0.0
 * @description Vista para mostrar datos de bitácora de instituciones
 * @author
 * @changelog
 * 1.0.0 - 2024/01/01 - @Jorel254 - Versión inicial
 */

import { computed, onMounted, ref, watch } from 'vue'
import BaseModal from '@/components/BaseModal.vue'
import { useStore } from 'vuex'
import { apiService } from '@/services/apiService'
import { getInstitutionCounter } from '@/components/utils/functions'
/**
 * @description Props del componente
 */

const error = ref<string | null>(null)
const submitting = ref(false)
const successMessage = ref<string | null>(null)
const showErrorModal = ref(false)
const showSuccessModal = ref(false)
const excelFileComplementary = ref<File | undefined>(undefined)
const processingExcelComplementary = ref(false)
const store = useStore()

const currentInstitutionId = ref<number>(0)
const idInstitution = ref<string>('')

onMounted(async () => {
	currentInstitutionId.value = await getInstitutionCounter()
})
let complementaryInstitutionData = ref<any | null>(null)

/**
 * @description Procesa el archivo Excel cuando se selecciona
 */
const handleExcelFileComplementaryChange = async () => {
	if (!excelFileComplementary.value) {
		return
	}

	const file = excelFileComplementary.value
	if (!file) {
		return
	}

	processingExcelComplementary.value = true
	error.value = null

	try {
		const reader = new FileReader()

		reader.onload = async e => {
			try {
				const arrayBuffer = e.target?.result as ArrayBuffer
				const base64 = btoa(String.fromCodePoint(...new Uint8Array(arrayBuffer)))

				const result = await apiService.post<{
					code: number
					message: string
					data: {
						complementaryUpdates: any
					}
				}>('processInstitutionExcelComplementary', {
					excelBase64: base64
				})

				if (result.code === 1 && result.data) {
					complementaryInstitutionData.value = result.data.complementaryUpdates.updates
					idInstitution.value = result.data.complementaryUpdates.idInstitution
					successMessage.value = 'Excel procesado correctamente. Los datos complementarios han sido cargados.'
					showSuccessModal.value = true
				} else {
					throw new Error(result.message || 'Error al procesar el archivo Excel')
				}
			} catch (err: any) {
				console.error('Error al procesar el Excel:', err)
				error.value = err.message || 'Error al procesar el archivo Excel'
			} finally {
				processingExcelComplementary.value = false
			}
		}

		reader.onerror = () => {
			error.value = 'Error al leer el archivo Excel'
			processingExcelComplementary.value = false
		}

		reader.readAsArrayBuffer(file)
	} catch (err: any) {
		console.error('Error al procesar el Excel:', err)
		error.value = err.message || 'Error al procesar el archivo Excel'
		processingExcelComplementary.value = false
	}
}

watch(
	excelFileComplementary,
	newFiles => {
		if (newFiles) {
			handleExcelFileComplementaryChange()
		} else {
			complementaryInstitutionData.value = null
		}
	},
	{ deep: true }
)

const copyToClipboard = (data: any) => {
	navigator.clipboard.writeText(JSON.stringify(data, null, 2))
	successMessage.value = 'Datos copiados al portapapeles'
	showSuccessModal.value = true
}

/**
 * @description Formatea JSON con colores para visualización mejorada
 */
const formatJsonWithColors = (data: any): string => {
	if (!data) {
		return '<span class="json-null">No hay datos de la institución</span>'
	}

	const jsonString = JSON.stringify(data, null, 2)

	// Escapar HTML para seguridad
	const escapeHtml = (text: string) => {
		const map: Record<string, string> = {
			'&': '&amp;',
			'<': '&lt;',
			'>': '&gt;',
			'"': '&quot;',
			"'": '&#039;'
		}
		return text.replace(/[&<>"']/g, m => map[m])
	}

	// Aplicar colores usando regex (procesar en orden específico)
	let formatted = escapeHtml(jsonString)

	// Colorear strings primero (para evitar conflictos con otros elementos)
	formatted = formatted.replace(/"([^"\\]*(\\.[^"\\]*)*)"/g, '<span class="json-string">"$1"</span>')

	formatted = formatted.replace(/\bnull\b(?!<span[^>]{0,50}>)/g, '<span class="json-null">null</span>')
	formatted = formatted.replace(/\b(true|false)\b(?!<span[^>]{0,50}>)/g, '<span class="json-boolean">$1</span>')
	formatted = formatted.replace(/\b(-?\d+\.?\d*)\b(?!<span[^>]{0,50}>)/g, '<span class="json-number">$1</span>')

	// Colorear llaves, corchetes, dos puntos y comas (solo fuera de tags span)
	formatted = formatted.replace(/(\{|\})(?!<span[^>]{0,50}>)/g, '<span class="json-brace">$1</span>')
	formatted = formatted.replace(/(\[|\])(?!<span[^>]{0,50}>)/g, '<span class="json-bracket">$1</span>')
	formatted = formatted.replace(/:(?!<span[^>]{0,50}>)/g, '<span class="json-colon">:</span>')
	formatted = formatted.replace(/,(?!<span[^>]{0,50}>)/g, '<span class="json-comma">,</span>')

	return formatted
}

const saveComplementaryChanges = async () => {
	try {
		submitting.value = true
		error.value = null

		// Obtener el ID de la institución actual
		const institutionId = store.getters['global/currentInstitution']
		if (!institutionId) {
			throw new Error('No hay una institución seleccionada')
		}

		// Validar que hay datos nuevos para actualizar
		if (!complementaryInstitutionData.value) {
			throw new Error('No hay datos de institución para actualizar. Por favor, carga un archivo Excel primero.')
		}
		if (idInstitution.value !== store.state.global.selectedInstitution.idNombreInstitucion) {
			error.value = `El ID de nombre de la institución no coincide con el ID de nombre de la institución que queremos actualizar.`
			showErrorModal.value = true
			return
		}
		// Actualizar usando el servicio
		let fieldsUpdated = 0
		try {
			const response = await apiService.post<{ code: number; message: string; data?: { fieldsUpdated: number } }>('updateComplementaryInstitutionData', {
				institutionId: Number(institutionId),
				updates: complementaryInstitutionData.value
			})

			if (response.code === -1) {
				throw new Error(response.message || 'Error al actualizar la institución')
			}

			fieldsUpdated = response.data?.fieldsUpdated ?? 0
		} catch (error) {
			console.error('Error al actualizar la institución:', error)
			throw new Error(`Error al actualizar los datos de la institución: ${(error as Error).message}`)
		}

		if (fieldsUpdated === 0) {
			successMessage.value = 'No hay campos nuevos para actualizar. Todos los datos ya existen en la institución.'
			showSuccessModal.value = true
		} else {
			successMessage.value = `Institución actualizada exitosamente. Se agregaron ${fieldsUpdated} campo(s) nuevo(s).`
			showSuccessModal.value = true
		}
		setTimeout(() => {
			globalThis.location.reload()
		}, 6000)
	} catch (err: any) {
		console.error('Error al actualizar la institución:', err)
		error.value = err.message || 'Error al actualizar los datos de la institución'
		showErrorModal.value = true
	} finally {
		submitting.value = false
	}
}

const selectedInstitution = computed(() => store.state.global.selectedInstitution)
const carreraData = computed(() => selectedInstitution.value?.Carrera)
</script>

<template>
	<div class="creditos">
		<VaCard class="bitacora-card">
			<VaCardTitle class="header-gradient">
				<div class="header-content">
					<div class="header-main">
						<div class="header-icon-wrapper">
							<va-icon name="grading" class="header-icon" />
						</div>
						<div class="header-text">
							<h2 class="header-title">Importación de créditos</h2>
							<p class="header-subtitle">Carga el Excel complementario de créditos, revisa los datos generados y actualiza las asignaturas de la institución.</p>
						</div>
						<va-button class="action-button action-button--save" :loading="submitting" :disabled="submitting || processingExcelComplementary" @click="saveComplementaryChanges()">
							<va-icon name="save" class="button-icon" />
							Guardar cambios
						</va-button>
					</div>
				</div>
			</VaCardTitle>

			<VaCardContent class="card-content">
				<div class="pe-toolbar">
					<div class="pe-toolbar__meta">
						<span class="pe-chip">Institución #{{ currentInstitutionId || '—' }}</span>
						<span class="pe-chip pe-chip--accent">ID: {{ selectedInstitution?.idNombreInstitucion || 'Sin dato' }}</span>
						<p class="pe-hint">Procesa el archivo Excel de créditos y compáralo con las carreras actuales antes de guardar.</p>
					</div>
				</div>

				<section class="pe-section">
					<div class="pe-section__head">
						<va-icon name="upload_file" class="pe-section__icon" />
						<h3 class="pe-section__title">Carga de archivo</h3>
					</div>
					<article class="pe-panel pe-panel--upload">
						<div class="pe-panel__head">
							<va-icon name="table_chart" class="pe-panel__icon" />
							<div>
								<h4 class="pe-panel__title">Archivo Excel de créditos</h4>
								<p class="pe-panel__desc">Carga el Excel complementario para actualizar créditos en las asignaturas.</p>
							</div>
						</div>
						<div class="pe-panel__body">
							<VaFileUpload
								v-model="excelFileComplementary"
								uploadButtonText="Cargar excel de créditos"
								color="success"
								type="single"
								fileTypes=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
								:disabled="submitting || processingExcelComplementary"
								class="upload-button" />
							<div v-if="processingExcelComplementary" class="processing-indicator">
								<va-icon name="hourglass_empty" spin class="processing-icon" />
								<span class="processing-text">Procesando Excel de créditos…</span>
							</div>
						</div>
					</article>
				</section>

				<section class="pe-section">
					<div class="pe-section__head">
						<va-icon name="compare_arrows" class="pe-section__icon" />
						<h3 class="pe-section__title">Comparación de datos</h3>
					</div>
					<div class="pe-compare">
						<div class="pe-compare__col">
							<div class="pe-json-header">
								<div class="pe-json-header__text">
									<span class="pe-json-header__label">Datos a complementar</span>
									<VaBadge :text="`ID: ${idInstitution || 'Sin dato'}`" color="success" size="small" />
								</div>
								<va-button v-if="complementaryInstitutionData" preset="secondary" size="small" class="pe-copy-btn" @click="copyToClipboard(complementaryInstitutionData)">
									<va-icon name="content_copy" />
									Copiar
								</va-button>
							</div>
							<div class="json-viewer" v-html="formatJsonWithColors(complementaryInstitutionData)"></div>
						</div>

						<div class="pe-compare__col">
							<div class="pe-json-header">
								<div class="pe-json-header__text">
									<span class="pe-json-header__label">Carreras actuales</span>
									<VaBadge :text="`ID: ${selectedInstitution?.idNombreInstitucion || 'Sin dato'}`" color="info" size="small" />
								</div>
								<va-button v-if="carreraData" preset="secondary" size="small" class="pe-copy-btn" @click="copyToClipboard(carreraData)">
									<va-icon name="content_copy" />
									Copiar
								</va-button>
							</div>
							<div class="json-viewer" v-html="formatJsonWithColors(carreraData)"></div>
						</div>
					</div>
				</section>
			</VaCardContent>
		</VaCard>

		<BaseModal v-model="showErrorModal" title="Error" :message="error || 'Error al guardar los datos'" :test-mode="true" size="medium">
			<template #body>
				<div class="error-message">
					<div class="error-header">
						<div class="error-icon">
							<i class="fas fa-exclamation-triangle"></i>
						</div>
						<h3 class="error-title">Error</h3>
					</div>
					<p class="error-description">{{ error || 'Error al guardar los datos' }}</p>
				</div>
			</template>
			<template #footer>
				<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
			</template>
		</BaseModal>
		<BaseModal v-model="showSuccessModal" title="" :message="successMessage || 'Datos guardados correctamente'" :test-mode="false" size="medium">
			<template #body>
				<div class="success-message">
					<div class="success-header">
						<div class="success-icon">
							<i class="fas fa-check-circle"></i>
						</div>
						<h3 class="success-title">Éxito</h3>
					</div>
					<p class="success-description">{{ successMessage || 'Datos actualizados correctamente' }}</p>
				</div>
			</template>
			<template #footer>
				<button class="btn btn-primary" @click="showSuccessModal = false">Entendido</button>
			</template>
		</BaseModal>
	</div>
</template>

<style scoped>
.creditos {
	--pe-navy: #0f3569;
	--pe-navy-deep: #062342;
	--pe-accent: #24c8ff;
	--pe-accent-2: #169bff;
	--pe-border: rgba(15, 53, 105, 0.12);
	--pe-surface: #ffffff;
	--pe-canvas: #f4f7fb;
	--pe-text: #0f3569;
	--pe-text-2: rgba(15, 53, 105, 0.72);
	--pe-radius: 16px;
	--pe-radius-sm: 12px;
	--pe-shadow: 0 8px 24px rgba(15, 53, 105, 0.08);
	width: 100%;
	max-width: 100%;
	min-width: 0;
	overflow-x: hidden;
	box-sizing: border-box;
}

.bitacora-card {
	border-radius: var(--pe-radius);
	box-shadow: var(--pe-shadow);
	animation: fadeInUp 0.5s ease-out;
	overflow: hidden;
	background: var(--pe-surface);
	border: 1px solid var(--pe-border);
	width: 100%;
	max-width: 100%;
	min-width: 0;
}

.header-gradient {
	display: block;
	width: 100%;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	padding: 1.25rem 1.5rem;
	border-radius: 0;
	position: relative;
	text-transform: none;
	letter-spacing: normal;
	font-weight: 400;
}

.header-gradient::before {
	content: '';
	position: absolute;
	inset: 0;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	pointer-events: none;
}

.header-content {
	position: relative;
	z-index: 1;
	width: 100%;
}

.header-main {
	display: flex;
	align-items: center;
	gap: 1.25rem;
	flex-wrap: wrap;
	width: 100%;
}

.header-icon-wrapper {
	width: 56px;
	height: 56px;
	border-radius: 14px;
	background: rgba(255, 255, 255, 0.14);
	backdrop-filter: blur(8px);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	border: 1px solid rgba(255, 255, 255, 0.18);
}

.header-icon {
	color: white;
	font-size: 1.75rem;
}

.header-text {
	flex: 1;
	min-width: 200px;
}

.header-title {
	font-size: 1.5rem;
	font-weight: 700;
	color: white;
	margin: 0;
	letter-spacing: -0.02em;
	text-transform: none;
	line-height: 1.2;
}

.header-subtitle {
	margin: 0.35rem 0 0;
	font-size: 0.85rem;
	color: rgba(255, 255, 255, 0.78);
	line-height: 1.45;
	max-width: 52ch;
	text-transform: none;
	letter-spacing: normal;
	font-weight: 400;
}

.action-button--save {
	margin-left: auto;
	min-width: 190px;
	background: linear-gradient(90deg, var(--pe-accent-2), var(--pe-accent)) !important;
	border: none !important;
	font-weight: 600;
}

.card-content {
	padding: 1.5rem;
	background: var(--pe-canvas);
	overflow-x: hidden;
	overflow-y: auto;
	max-height: calc(100vh - 200px);
	min-width: 0;
}

.card-content::-webkit-scrollbar {
	width: 8px;
}

.card-content::-webkit-scrollbar-track {
	background: #e8eef5;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb {
	background: #9eb3cc;
	border-radius: 4px;
}

.pe-toolbar {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 1.25rem;
	padding-bottom: 1rem;
	border-bottom: 1px solid var(--pe-border);
}

.pe-toolbar__meta {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5rem;
}

.pe-chip {
	display: inline-flex;
	align-items: center;
	padding: 0.3rem 0.75rem;
	border-radius: 999px;
	font-size: 0.75rem;
	font-weight: 600;
	color: var(--pe-text);
	background: rgba(15, 53, 105, 0.06);
	border: 1px solid var(--pe-border);
}

.pe-chip--accent {
	color: var(--pe-navy-deep);
	background: rgba(36, 200, 255, 0.12);
	border-color: rgba(36, 200, 255, 0.28);
}

.pe-hint {
	flex-basis: 100%;
	margin: 0.35rem 0 0;
	font-size: 0.875rem;
	color: var(--pe-text-2);
	line-height: 1.5;
}

.pe-section + .pe-section {
	margin-top: 1.5rem;
	padding-top: 1.5rem;
	border-top: 1px solid var(--pe-border);
}

.pe-section__head {
	display: flex;
	align-items: center;
	gap: 0.6rem;
	margin-bottom: 1rem;
}

.pe-section__icon {
	color: var(--pe-navy-deep);
	font-size: 1.25rem;
}

.pe-section__title {
	margin: 0;
	font-size: 1.05rem;
	font-weight: 700;
	color: var(--pe-text);
	letter-spacing: -0.01em;
}

.pe-panel {
	display: flex;
	flex-direction: column;
	min-width: 0;
	background: var(--pe-surface);
	border: 1px solid var(--pe-border);
	border-radius: var(--pe-radius-sm);
	padding: 1rem 1.1rem;
	transition:
		border-color 0.2s ease,
		box-shadow 0.2s ease;
}

.pe-panel--upload {
	max-width: min(520px, 100%);
}

.pe-panel:hover {
	border-color: rgba(36, 200, 255, 0.35);
	box-shadow: 0 4px 14px rgba(15, 53, 105, 0.06);
}

.pe-panel__head {
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	margin-bottom: 0.85rem;
	padding-bottom: 0.75rem;
	border-bottom: 1px solid rgba(15, 53, 105, 0.08);
}

.pe-panel__icon {
	flex-shrink: 0;
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 10px;
	font-size: 1.1rem;
	color: var(--pe-navy-deep);
	background: rgba(36, 200, 255, 0.1);
}

.pe-panel__title {
	margin: 0;
	font-size: 0.95rem;
	font-weight: 700;
	color: var(--pe-text);
}

.pe-panel__desc {
	margin: 0.2rem 0 0;
	font-size: 0.78rem;
	color: var(--pe-text-2);
	line-height: 1.4;
}

.pe-panel__body {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	min-width: 0;
	overflow-wrap: anywhere;
}

.pe-compare {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 1rem;
	align-items: start;
	width: 100%;
	min-width: 0;
}

.pe-compare__col {
	min-width: 0;
	max-width: 100%;
}

.pe-json-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	margin-bottom: 0.65rem;
}

.pe-json-header__text {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.5rem;
}

.pe-json-header__label {
	font-size: 0.875rem;
	font-weight: 700;
	color: var(--pe-text);
}

.pe-copy-btn {
	flex-shrink: 0;
}

.upload-button {
	width: 100%;
	max-width: 100%;
	min-width: 0;
}

.upload-button :deep(.va-file-upload),
.upload-button :deep(.va-file-upload__field),
.upload-button :deep(.va-button) {
	max-width: 100%;
}

.upload-button :deep(.va-button__content) {
	white-space: normal;
	text-align: center;
	line-height: 1.3;
}

.processing-indicator {
	display: flex;
	align-items: center;
	gap: 0.65rem;
	padding: 0.75rem 0.9rem;
	background: rgba(36, 200, 255, 0.08);
	border-radius: 10px;
	border-left: 3px solid var(--pe-navy-deep);
}

.processing-icon {
	color: var(--pe-navy-deep);
	font-size: 1.1rem;
}

.processing-text {
	color: var(--pe-navy-deep);
	font-weight: 600;
	font-size: 0.875rem;
}

.json-viewer {
	padding: 1rem;
	background: #1a1f2e;
	border-radius: 10px;
	font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
	font-size: 0.8rem;
	line-height: 1.55;
	color: #e8e8e8;
	overflow: auto;
	max-width: 100%;
	max-height: 420px;
	border: 1px solid rgba(15, 53, 105, 0.25);
	white-space: pre;
}

.json-viewer::-webkit-scrollbar {
	width: 8px;
	height: 8px;
}

.json-viewer::-webkit-scrollbar-track {
	background: #252b3d;
	border-radius: 4px;
}

.json-viewer::-webkit-scrollbar-thumb {
	background: #4a5568;
	border-radius: 4px;
}

.json-viewer :deep(.json-string) {
	color: #ce9178;
}

.json-viewer :deep(.json-number) {
	color: #b5cea8;
}

.json-viewer :deep(.json-boolean) {
	color: #569cd6;
	font-weight: 600;
}

.json-viewer :deep(.json-null) {
	color: #808080;
	font-style: italic;
}

.json-viewer :deep(.json-brace),
.json-viewer :deep(.json-bracket),
.json-viewer :deep(.json-colon),
.json-viewer :deep(.json-comma) {
	color: #d4d4d4;
}

.json-viewer :deep(.json-brace),
.json-viewer :deep(.json-bracket) {
	font-weight: 600;
}

.json-viewer :deep(.json-colon) {
	margin: 0 2px;
}

.button-icon {
	margin-right: 0.35rem;
}

.error-description,
.success-description {
	font-size: 1rem;
	line-height: 1.6;
	margin-bottom: 1rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
}

.error-description {
	border-left: 4px solid #f71d1d;
}

.success-description {
	border-left: 4px solid #28a745;
}

.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 1px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 24px rgba(254, 178, 178, 0.25);
	animation: fadeInUp 0.35s ease-out;
}

.success-message {
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border: 1px solid #28a745;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 24px rgba(40, 167, 69, 0.2);
	animation: fadeInUp 0.35s ease-out;
}

.error-header,
.success-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1rem;
	padding-bottom: 1rem;
	border-bottom: 1px solid rgba(255, 255, 255, 0.35);
}

.error-icon,
.success-icon {
	width: 44px;
	height: 44px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
}

.error-icon {
	background: #e67a7a;
	box-shadow: 0 4px 12px rgba(245, 101, 101, 0.35);
}

.success-icon {
	background: #1e7e34;
	box-shadow: 0 4px 12px rgba(40, 167, 69, 0.35);
}

.error-icon i,
.success-icon i {
	color: white;
	font-size: 1.35rem;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.35rem;
	font-weight: 700;
}

.success-title {
	margin: 0;
	color: #ffffff;
	font-size: 1.35rem;
	font-weight: 700;
}

:deep(.va-tabs) {
	width: 100%;
}

:deep(.va-tabs__content) {
	width: 100%;
	display: block;
}

:deep(.va-tabs__content > .va-card) {
	width: 100% !important;
	max-width: 100% !important;
	margin-left: 0 !important;
	margin-right: 0 !important;
}

@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(16px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@media (max-width: 1200px) {
	.pe-compare {
		grid-template-columns: 1fr;
	}
}

@media (max-width: 768px) {
	.card-content {
		padding: 1rem;
	}

	.header-gradient {
		padding: 1rem;
	}

	.header-main {
		flex-direction: column;
		align-items: stretch;
	}

	.action-button--save {
		margin-left: 0;
		width: 100%;
	}

	.pe-panel--upload {
		max-width: none;
	}

	.pe-json-header {
		flex-direction: column;
		align-items: flex-start;
	}
}
</style>
